import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
    documentBundlesTemplate,
    saveDocumentBundleTemplate,
    updateDocumentBundleTemplate,
    deleteDocumentBundleTemplate,
} from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { DocumentBundle, DocumentBundleInput } from 'models/organization';

type DocumentBundlesContextType = {
    documentBundles: DocumentBundle[];
    saveDocumentBundle: (variables: { documentBundle: DocumentBundleInput }) => Promise<boolean>;
    updateDocumentBundle: (variables: { documentBundle: DocumentBundleInput }) => Promise<boolean>;
    deleteDocumentBundle: (variables: { documentBundleId: string }) => Promise<boolean>;
    documentBundlesLoading: boolean;
    saveDocumentBundleLoading: boolean;
    updateDocumentBundleLoading: boolean;
    deleteDocumentBundleLoading: boolean;
};

const DocumentBundlesContext = createContext<DocumentBundlesContextType | undefined>(undefined);

export function DocumentBundlesProvider({ children }: { children: ReactNode }) {
    const { showBoundary } = useDenthubErrorBoundary();

    const {
        data: documentBundlesData,
        loading: documentBundlesLoading,
        refetch: refetchDocumentBundles,
    } = useQuery(documentBundlesTemplate, {
        fetchPolicy: 'no-cache',
    });

    const [saveDocumentBundleMutation, { loading: saveDocumentBundleLoading }] =
        useMutation(saveDocumentBundleTemplate);
    const [updateDocumentBundleMutation, { loading: updateDocumentBundleLoading }] =
        useMutation(updateDocumentBundleTemplate);
    const [deleteDocumentBundleMutation, { loading: deleteDocumentBundleLoading }] =
        useMutation(deleteDocumentBundleTemplate);

    const saveDocumentBundle = async (variables: { documentBundle: DocumentBundleInput }) => {
        try {
            const result = await saveDocumentBundleMutation({ variables });
            await refetchDocumentBundles();
            return result.data.saveDocumentBundle;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const updateDocumentBundle = async (variables: { documentBundle: DocumentBundleInput }) => {
        try {
            const result = await updateDocumentBundleMutation({ variables });
            await refetchDocumentBundles();
            return result.data.updateDocumentBundle;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const deleteDocumentBundle = async (variables: { documentBundleId: string }) => {
        try {
            const result = await deleteDocumentBundleMutation({ variables });
            await refetchDocumentBundles();
            return result.data.deleteDocumentBundle;
        } catch (err) {
            showBoundary(err);
            return false;
        }
    };

    const value = useMemo(
        () => ({
            documentBundles: documentBundlesData?.documentBundles ?? [],
            saveDocumentBundle,
            updateDocumentBundle,
            deleteDocumentBundle,
            documentBundlesLoading,
            saveDocumentBundleLoading,
            updateDocumentBundleLoading,
            deleteDocumentBundleLoading,
        }),
        [
            documentBundlesData,
            documentBundlesLoading,
            saveDocumentBundleLoading,
            updateDocumentBundleLoading,
            deleteDocumentBundleLoading,
        ],
    );

    return <DocumentBundlesContext.Provider value={value}>{children}</DocumentBundlesContext.Provider>;
}

export function useDocumentBundles() {
    const context = useContext(DocumentBundlesContext);
    if (!context) {
        throw new Error('useDocumentBundles must be used within a DocumentBundlesProvider');
    }
    return context;
}
